<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Task Grades') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'ProjectSettings|Task grades allow you to evaluate and rate completed tasks. This feature helps track quality, performance, and provides feedback on task completion.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|How It Works') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|When enabled, project members can assign grades to tasks after they are completed. Grades can be used to assess the quality of work, track performance over time, and provide structured feedback.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Grade Scales') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|You can create custom grading scales for your project. Each scale consists of grades with values (like "A+", "B", "5", or "Excellent") and optional descriptions. Grades are ordered by position, with 0 being the highest/best grade.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Example Grade Scales') }}</h4>

            <div
              style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 8px"
            >
              <strong style="color: #333; display: block; margin-bottom: 8px">
                {{ s__('ProjectSettings|Example 1: Letter Grades') }}
              </strong>
              <div style="font-size: 14px; color: #666; line-height: 1.8">
                <div><strong>A+</strong> - {{ s__('ProjectSettings|Exceptional work') }}</div>
                <div><strong>A</strong> - {{ s__('ProjectSettings|Excellent quality') }}</div>
                <div><strong>B</strong> - {{ s__('ProjectSettings|Good work') }}</div>
                <div><strong>C</strong> - {{ s__('ProjectSettings|Acceptable') }}</div>
                <div><strong>D</strong> - {{ s__('ProjectSettings|Needs improvement') }}</div>
              </div>
            </div>

            <div
              style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 8px"
            >
              <strong style="color: #333; display: block; margin-bottom: 8px">
                {{ s__('ProjectSettings|Example 2: Numeric Scale') }}
              </strong>
              <div style="font-size: 14px; color: #666; line-height: 1.8">
                <div><strong>5</strong> - {{ s__('ProjectSettings|Outstanding') }}</div>
                <div><strong>4</strong> - {{ s__('ProjectSettings|Above average') }}</div>
                <div><strong>3</strong> - {{ s__('ProjectSettings|Satisfactory') }}</div>
                <div><strong>2</strong> - {{ s__('ProjectSettings|Below expectations') }}</div>
                <div><strong>1</strong> - {{ s__('ProjectSettings|Unsatisfactory') }}</div>
              </div>
            </div>

            <h4>{{ s__('ProjectSettings|Use Cases') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|Task grades are useful for performance reviews, quality assurance, learning and development tracking, and identifying areas that need improvement. They provide a structured way to evaluate completed work.'
                )
              }}
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('ProjectSettings|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}
</style>
